// ---------------------------------------------------------------------------
// Iter 62cm — PWA install banner
// ---------------------------------------------------------------------------
// Shows a compact banner encouraging users to install ASA to their home screen
// (iOS Safari) or desktop dock (Chrome/Edge). Auto-dismisses when installed.
// Dismissed banner is remembered in localStorage for 7 days.
// ---------------------------------------------------------------------------
import { useEffect, useState } from 'react';

const DISMISS_KEY = 'asa_install_banner_dismissed_until';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    window.navigator?.standalone === true
  );
}

function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isMac = /Macintosh/.test(ua) && !isIOS;
  return { isIOS, isSafari, isMac, isMobile: /Mobi|Android/i.test(ua) };
}

const GUEST_SEEN_KEY = 'asa_guest_install_guide_seen';
const isGuestDevice = () => { try { return !!localStorage.getItem('asa_guest_link'); } catch { return false; } };

export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const { isIOS, isSafari, isMac, isMobile } = detectPlatform();
  const guest = isGuestDevice() && isMobile;

  useEffect(() => {
    if (isStandalone()) return; // Already installed

    // Invités sur mobile : guide « Installe l'app en 2 gestes » ouvert dès la 1re connexion
    if (isGuestDevice() && isMobile) {
      // Pop-up centré (pas de bannière en bas qui gêne le postage) : 1re connexion puis tous les 3 jours max.
      const t = setTimeout(() => {
        try {
          const last = Number(localStorage.getItem(GUEST_SEEN_KEY) || 0);
          if (last > 1 && Date.now() - last < 3 * 86400000) return;
          localStorage.setItem(GUEST_SEEN_KEY, String(Date.now()));
        } catch { /* noop */ }
        setVisible(true); setShowIOSHelp(true);
      }, 1500);
      const h = (e) => { e.preventDefault(); setDeferredPrompt(e); };
      window.addEventListener('beforeinstallprompt', h);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', h); };
    }

    // Respect dismiss window
    try {
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && Date.now() < until) return;
    } catch { /* noop */ }

    // Chrome / Edge / Android : `beforeinstallprompt` fires
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari : no prompt event exists → show a manual guide banner on load
    if (isIOS && isSafari) {
      // Small delay so the first paint isn't cluttered
      setTimeout(() => setVisible(true), 1200);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isIOS, isSafari]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    } catch { /* noop */ }
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      // Native Chrome / Edge / Android prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice.catch(() => ({ outcome: 'dismissed' }));
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } else if ((isIOS && isSafari) || guest) {
      setShowIOSHelp(true);
    }
  };

  if (!visible) return null;
  if (guest) return showIOSHelp ? <GuestInstallPopup isIOS={isIOS} isSafari={isSafari} canPrompt={!!deferredPrompt} onInstall={install} onClose={() => { setShowIOSHelp(false); setVisible(false); }} /> : null;

  return (
    <div
      data-testid="pwa-install-banner"
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)',
        left: 12,
        right: 12,
        zIndex: 9998,
        background: 'linear-gradient(180deg, rgba(30,38,68,0.98) 0%, rgba(20,26,48,0.98) 100%)',
        border: '1px solid rgba(91,190,252,0.28)',
        borderRadius: 16,
        padding: '14px 14px 14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(62,123,250,0.4)',
      }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 900, fontSize: 18, color: '#0B1330' }}>A</span>
      </div>
      <div style={{ flex: 1, minWidth: 0, color: '#EDF1FB' }}>
        <div style={{
          fontFamily: 'Sora, -apple-system', fontWeight: 800, fontSize: 13.5,
          lineHeight: 1.25,
        }}>
          {guest ? 'Installe l’app en 2 gestes' : isIOS ? 'Ajoute ASA à ton écran d’accueil' : isMac ? 'Installe ASA dans ton dock' : 'Installe ASA en 1 clic'}
        </div>
        <div style={{
          fontFamily: 'Lexend, -apple-system', fontSize: 11, color: '#A5AFC7',
          marginTop: 2,
        }}>
          {guest ? 'Tes tâches en 1 tap, sans code ni compte' : isIOS ? 'Notifs iPhone + fullscreen + accès direct' : 'Notifs + fenêtre dédiée + accès rapide'}
        </div>
      </div>
      <button
        data-testid="pwa-install-btn"
        onClick={install}
        style={{
          padding: '9px 14px',
          borderRadius: 999,
          background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
          color: '#0B1330',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(62,123,250,0.35)',
          flexShrink: 0,
        }}
      >
        Installer
      </button>
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Fermer"
        style={{
          padding: 6,
          borderRadius: 999,
          background: 'transparent',
          border: 'none',
          color: '#A5AFC7',
          fontSize: 18,
          fontWeight: 700,
          cursor: 'pointer',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>

      {/* iOS help sheet */}
      {showIOSHelp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
            padding: 20,
          }}
          onClick={() => setShowIOSHelp(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, #1a2242 0%, #101632 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 18,
              padding: 22,
              maxWidth: 340,
              color: '#EDF1FB',
              fontFamily: 'Lexend, -apple-system',
            }}
          >
            <div data-testid="pwa-guide-title" style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17, marginBottom: 14 }}>
              {guest ? 'Installe l’app en 2 gestes' : 'Ajoute ASA en 3 étapes'}
            </div>
            {guest ? (
              <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: 1.55 }}>
                {isIOS ? (<>
                  <li>Touche <b>Partager</b> (carré avec flèche vers le haut){isSafari ? ' en bas de Safari' : ''}</li>
                  <li>Touche <b>« Sur l’écran d’accueil »</b> puis <b>Ajouter</b></li>
                </>) : (<>
                  <li>Touche le menu <b>⋮</b> en haut à droite du navigateur</li>
                  <li>Touche <b>« Installer l’application »</b> (ou « Ajouter à l’écran d’accueil »)</li>
                </>)}
              </ol>
            ) : <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: 1.55 }}>
              <li>Touche le bouton <b>Partager</b> en bas de Safari (carré avec flèche vers le haut)</li>
              <li>Fais défiler et touche <b>« Sur l’écran d’accueil »</b></li>
              <li>Touche <b>Ajouter</b> en haut à droite</li>
            </ol>}
            <div style={{
              marginTop: 14,
              padding: '10px 12px',
              background: 'rgba(52,211,153,0.10)',
              border: '1px solid rgba(52,211,153,0.28)',
              borderRadius: 10,
              fontSize: 11.5,
              color: '#34D399',
            }}>
              {guest ? '✓ Ensuite, ouvre ASA depuis l’icône : tu arrives direct sur tes tâches, sans code.' : '✓ Ensuite, ouvre ASA depuis l’icône : les notifs Calendly arrivent comme une vraie app iPhone.'}
            </div>
            <button
              onClick={() => setShowIOSHelp(false)}
              style={{
                marginTop: 14, width: '100%',
                padding: '10px 0',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
                color: '#0B1330',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              J’ai compris
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GuestInstallPopup({ isIOS, isSafari, canPrompt, onInstall, onClose }) {
  return (
    <div data-testid="pwa-guest-popup" onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(3,6,18,0.72)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 340, maxHeight: '85vh', overflowY: 'auto',
        background: 'linear-gradient(180deg, #1a2242 0%, #101632 100%)',
        border: '1px solid rgba(91,190,252,0.25)', borderRadius: 20, padding: 22,
        color: '#EDF1FB', fontFamily: 'Lexend, -apple-system', boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 900, fontSize: 17, color: '#0B1330' }}>A</span>
          </div>
          <div data-testid="pwa-guide-title" style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17 }}>Installe l’app en 2 gestes</div>
        </div>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: 1.55 }}>
          {isIOS ? (<>
            <li>Touche <b>Partager</b> (carré avec flèche vers le haut){isSafari ? ' en bas de Safari' : ''}</li>
            <li>Touche <b>« Sur l’écran d’accueil »</b> puis <b>Ajouter</b></li>
          </>) : (<>
            <li>Touche le menu <b>⋮</b> en haut à droite du navigateur</li>
            <li>Touche <b>« Installer l’application »</b> (ou « Ajouter à l’écran d’accueil »)</li>
          </>)}
        </ol>
        <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.28)', borderRadius: 10, fontSize: 11.5, color: '#34D399' }}>
          ✓ Ensuite, ouvre ASA depuis l’icône : tu arrives direct sur tes tâches, sans code.
        </div>
        {canPrompt && (
          <button data-testid="pwa-guest-install" onClick={onInstall} style={{ marginTop: 14, width: '100%', padding: '11px 0', borderRadius: 10, background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>Installer maintenant</button>
        )}
        <button data-testid="pwa-guest-close" onClick={onClose} style={{ marginTop: 10, width: '100%', padding: '10px 0', borderRadius: 10, background: canPrompt ? 'transparent' : 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', color: canPrompt ? '#A5AFC7' : '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, border: canPrompt ? '1px solid rgba(255,255,255,0.12)' : 'none', cursor: 'pointer' }}>{canPrompt ? 'Plus tard' : 'J’ai compris'}</button>
      </div>
    </div>
  );
}
